// @paddle/paddle-js memoises its script-load promise, so after one failed load every later
// initializePaddle call returns the same rejection. This module loads Paddle.js itself, with a
// fresh <script> per attempt, and calls initializePaddle only once the script is on the page.
import { getPaddleInstance, initializePaddle, type Paddle } from "@paddle/paddle-js"
import * as Sentry from "@sentry/browser"
import { PaddleEnvironment, PaddleToken } from "../../config"
import { RetriesExhaustedError, retryWithBackoff, type RetryPolicy } from "./retry"

export const PADDLE_SCRIPT_URL = "https://cdn.paddle.com/paddle/v2/paddle.js"

export const PADDLE_RETRY_POLICY: RetryPolicy = { maxAttempts: 3, baseDelayMs: 500 }

export interface EnsurePaddleOptions {
  readonly reportFailure: boolean
}

export type EnsurePaddle = (options: EnsurePaddleOptions) => Promise<Paddle | undefined>

const injectPaddleScript = async (): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    document.querySelectorAll(`script[src="${PADDLE_SCRIPT_URL}"]`).forEach((stale) => {
      stale.remove()
    })
    const script = document.createElement("script")
    script.src = PADDLE_SCRIPT_URL
    script.addEventListener("load", () => {
      if (getPaddleInstance()) {
        resolve()
      } else {
        reject(new Error("Paddle.js not available"))
      }
    })
    script.addEventListener("error", () => {
      reject(new Error("Failed to load Paddle.js - v1"))
    })
    document.head.appendChild(script)
  })
}

const loadPaddleScript = async (): Promise<void> => {
  if (!getPaddleInstance()) {
    await injectPaddleScript()
  }
}

/**
 * Loads Paddle.js under `policy` and initialises it. Rejects with {@link RetriesExhaustedError}
 * when the script fails to load on every attempt.
 */
export const loadPaddle = async (policy: RetryPolicy): Promise<Paddle> => {
  await retryWithBackoff(loadPaddleScript, policy)
  const paddle = await initializePaddle({ token: PaddleToken, environment: PaddleEnvironment })
  if (!paddle) {
    throw new Error("Paddle.js not available")
  }
  return paddle
}

/** Loads Paddle once through `load`, sharing a pending load and retrying a failed one on the next call; with
 * `reportFailure`, a failed load sends one Sentry event carrying the attempts accumulated across loads. */
export const createPaddleLoader = (load: () => Promise<Paddle>): EnsurePaddle => {
  let instance: Paddle | undefined
  let failedAttempts = 0
  let pending: Promise<Paddle | undefined> | undefined
  let shouldReportFailure = false

  return async ({ reportFailure }) => {
    if (instance) return instance

    shouldReportFailure ||= reportFailure
    pending ??= Promise.resolve()
      .then(load)
      .then((loaded) => {
        instance = loaded
        return loaded
      })
      .catch((error: unknown) => {
        const exhausted = error instanceof RetriesExhaustedError ? error : undefined
        failedAttempts += exhausted?.attempts ?? 1
        if (shouldReportFailure) {
          Sentry.captureMessage("Paddle initialization failed", {
            extra: { error: exhausted ? exhausted.lastError : error, attempts: failedAttempts },
          })
        }
        return undefined
      })
      .finally(() => {
        pending = undefined
        shouldReportFailure = false
      })
    return await pending
  }
}
